import { omit } from "lodash";
import moment from "moment";
import { toSelectOption } from "../../../components/Input";
import { SegmentedControlOption } from "../../../components/SegmentedControl";
import { TransactionType } from "../../../utils";
import { DateFormat, WindowType } from "../../../utils/types";
import { cashbackCategoryOf } from "../../../utils/cashback";
import { Category, TransactionTypeOptions } from "../../Transactions";
import { ProgressObject } from "../panels";
import {
  CASHBACK_CATEGORY_MISSING_REASON,
  CATEGORIES_FAILED_REASON,
  CATEGORIES_LOADING_REASON,
} from "./constants";
import {
  createDefaultProgress,
  TYPE_OPTIONS,
} from "../panels/progress/constants";
import { SavingDetails, SavingsTransactions } from "../types";
import {
  AddCashbackAvailability,
  ChartPoint,
  ProgressEntry,
  ProgressEntryKind,
  SavingProgress,
  ValueCompositionFigures,
} from "./types";

const toCents = (euros: number) => Math.round(euros * 100);

export const balanceMovingRowsOf = ({
  savingsTransactions,
}: Pick<SavingDetails, "savingsTransactions">): SavingsTransactions[] =>
  savingsTransactions?.filter(({ movesBalance }) => movesBalance) ?? [];

export const getTransactionType = (
  type: TransactionType,
  isIncome: boolean,
) => {
  if (type === TransactionType.TRANSFER) {
    if (isIncome) {
      return TYPE_OPTIONS.find(
        (option) => option.value === TransactionTypeOptions.TRANSFER_IN,
      )!;
    }
    return TYPE_OPTIONS.find(
      (option) => option.value === TransactionTypeOptions.TRANSFER_OUT,
    )!;
  }
  return TYPE_OPTIONS.find((option) => option.value === type)!;
};

export const getProgressObject = (
  progress: SavingsTransactions,
): ProgressObject => {
  return {
    ...omit(progress, [
      "type",
      "isIncome",
      "currencyCode",
      "date",
      "fromAccountId",
      "fromAccountName",
      "toAccountId",
      "toAccountName",
      "netAmount",
      "grossAmount",
      "categoryId",
      "categoryName",
      "subCategoryId",
      "subCategoryName",
    ]),
    amount: (progress.grossAmount ?? progress.netAmount).toString(),
    taxWithheld: progress.taxWithheld?.toString() ?? "",
    transactionDate: progress.date,
    transactionType: getTransactionType(progress.type, progress.isIncome),
    fromAccount: toSelectOption(
      progress.fromAccountId,
      progress.fromAccountName,
    ),
    toAccount: toSelectOption(progress.toAccountId, progress.toAccountName),
    category: toSelectOption(progress.categoryId, progress.categoryName),
    subCategory: toSelectOption(
      progress.subCategoryId,
      progress.subCategoryName,
    ),
  };
};

export const addCashbackDisabledReasonOf = ({
  isLoading,
  hasError,
  categories,
}: AddCashbackAvailability): string | null => {
  if (isLoading) {
    return CATEGORIES_LOADING_REASON;
  }
  if (hasError) {
    return CATEGORIES_FAILED_REASON;
  }
  return cashbackCategoryOf(categories)
    ? null
    : CASHBACK_CATEGORY_MISSING_REASON;
};

export const cashbackProgressOf = (
  { accountId, name }: Pick<SavingDetails, "accountId" | "name">,
  incomeCategories: Category[],
): ProgressObject | null => {
  const cashbackCategory = cashbackCategoryOf(incomeCategories);
  if (!cashbackCategory) {
    return null;
  }
  return {
    ...createDefaultProgress(),
    toAccount: toSelectOption(accountId, name),
    category: { value: cashbackCategory.value, label: cashbackCategory.label },
  };
};

// Income grows the balance but not net contributed, so the gap between the two lines is interest plus cashback.
const getEntryKind = (transaction: SavingsTransactions): ProgressEntryKind => {
  if (transaction.isCashback) {
    return "cashback";
  }
  if (transaction.type === TransactionType.INCOME) {
    return "interest";
  }
  if (transaction.isIncome) {
    return "deposit";
  }
  return "withdrawal";
};

export const dayOf = (date: string) => date.split("T")[0];

const HIDEABLE_WINDOW_YEARS: Partial<Record<WindowType, number>> = {
  [WindowType.Y3]: 3,
  [WindowType.Y5]: 5,
};

export const isHideableWindow = (window: WindowType): boolean =>
  HIDEABLE_WINDOW_YEARS[window] !== undefined;

// Mirrors the API's `windowStartIso`, so a hidden range is one whose window would hold the whole saving.
const windowStartOf = (years: number, today: string): string =>
  moment(today, DateFormat.ISO).subtract(years, "years").format(DateFormat.ISO);

export const visibleWindowOptions = (
  options: SegmentedControlOption<WindowType>[],
  firstDate: string | undefined,
  today: string,
): SegmentedControlOption<WindowType>[] => {
  if (!firstDate) {
    return options;
  }
  return options.filter(({ value }) => {
    const years = HIDEABLE_WINDOW_YEARS[value];
    return years === undefined || firstDate < windowStartOf(years, today);
  });
};

// Running net contributed at the end of the last day before the loaded window, read from the lifetime series.
export const netContributedBefore = (
  lifetimePoints: ChartPoint[],
  saving: SavingDetails,
): number => {
  const firstTransaction = saving.savingsTransactions?.[0];
  if (!firstTransaction) {
    return 0;
  }
  const windowStart = dayOf(firstTransaction.date);
  const lastPointBefore = lifetimePoints.findLast(
    (point) => point.date < windowStart,
  );
  return lastPointBefore?.netContributed ?? 0;
};

// Single pass (oldest → newest) so the chart series and the entries list share the same running balance; entries are then returned newest-first.
export const buildSavingProgress = (
  saving: SavingDetails,
  openingNetContributed = 0,
): SavingProgress => {
  const transactions = saving.savingsTransactions ?? [];

  let balanceCents = toCents(saving.transactionsBefore);
  let netContributedCents = toCents(openingNetContributed);
  let interestAvailableCents = 0;
  let cashbackAvailableCents = 0;
  let interestWithdrawnCents = 0;
  let cashbackWithdrawnCents = 0;
  // A day's credits count before its withdrawals: the clamp is order-dependent and the API has no same-day tie-breaker.
  let day = {
    date: "",
    opening: {
      netContributedCents,
      interestAvailableCents,
      cashbackAvailableCents,
      interestWithdrawnCents,
      cashbackWithdrawnCents,
    },
    depositedCents: 0,
    interestCents: 0,
    cashbackCents: 0,
    withdrawnCents: 0,
  };

  const chartPoints: ChartPoint[] = [];
  const entries: ProgressEntry[] = [];

  transactions.forEach((transaction) => {
    // Cashback paid into another account is listed but never touched this balance.
    if (!transaction.movesBalance) {
      return;
    }
    const amount = Number(transaction.netAmount);
    const amountCents = toCents(amount);
    const kind = getEntryKind(transaction);
    const dateLabel = dayOf(transaction.date);
    const creditedCashbackCents = transaction.cashbackCredited
      ? toCents(transaction.cashbackEarned ?? 0)
      : 0;

    if (transaction.isIncome) {
      balanceCents += amountCents;
    } else {
      balanceCents -= amountCents;
    }
    balanceCents += creditedCashbackCents;

    if (day.date !== dateLabel) {
      day = {
        date: dateLabel,
        opening: {
          netContributedCents,
          interestAvailableCents,
          cashbackAvailableCents,
          interestWithdrawnCents,
          cashbackWithdrawnCents,
        },
        depositedCents: 0,
        interestCents: 0,
        cashbackCents: 0,
        withdrawnCents: 0,
      };
    }

    if (kind === "deposit") {
      day.depositedCents += amountCents;
    } else if (kind === "withdrawal") {
      day.withdrawnCents += amountCents;
    } else if (kind === "cashback") {
      day.cashbackCents += amountCents;
    } else {
      day.interestCents += amountCents;
    }
    day.cashbackCents += creditedCashbackCents;

    const contributedCents =
      day.opening.netContributedCents + day.depositedCents;
    const interestPoolCents =
      day.opening.interestAvailableCents + day.interestCents;
    const cashbackPoolCents =
      day.opening.cashbackAvailableCents + day.cashbackCents;
    const excessCents = Math.max(day.withdrawnCents - contributedCents, 0);
    // Interest is drawn before cashback so cashback, earmarked for buys, is the last credit spent.
    const fromInterestCents = Math.min(excessCents, interestPoolCents);
    const fromCashbackCents = Math.min(
      excessCents - fromInterestCents,
      cashbackPoolCents,
    );

    netContributedCents = Math.max(contributedCents - day.withdrawnCents, 0);
    interestAvailableCents = interestPoolCents - fromInterestCents;
    cashbackAvailableCents = cashbackPoolCents - fromCashbackCents;
    // An overdraft past all three buckets stays on interest, so it reads as negative retained interest.
    interestWithdrawnCents =
      day.opening.interestWithdrawnCents + excessCents - fromCashbackCents;
    cashbackWithdrawnCents =
      day.opening.cashbackWithdrawnCents + fromCashbackCents;

    const balance = balanceCents / 100;
    entries.push({
      id: transaction.id,
      date: transaction.date,
      kind,
      amount: kind === "withdrawal" ? -amount : amount,
      balanceAfter: balance,
      transaction,
    });

    // Collapse multiple movements on the same day into a single chart point.
    const netContributed = netContributedCents / 100;
    const lastPoint = chartPoints.at(-1);
    if (lastPoint?.date === dateLabel) {
      lastPoint.balance = balance;
      lastPoint.netContributed = netContributed;
    } else {
      chartPoints.push({ date: dateLabel, balance, netContributed });
    }
  });

  entries.reverse();

  return {
    chartPoints,
    entries,
    interestWithdrawn: interestWithdrawnCents / 100,
    cashbackWithdrawn: cashbackWithdrawnCents / 100,
  };
};

export const buildValueComposition = (
  saving: SavingDetails,
  {
    interestWithdrawn,
    cashbackWithdrawn,
  }: Pick<SavingProgress, "interestWithdrawn" | "cashbackWithdrawn">,
): ValueCompositionFigures => {
  const interestWithdrawnCents = toCents(interestWithdrawn);
  const cashbackWithdrawnCents = toCents(cashbackWithdrawn);
  return {
    netContributed:
      (toCents(saving.investedValue) -
        toCents(saving.withdrawnValue) +
        interestWithdrawnCents +
        cashbackWithdrawnCents) /
      100,
    retainedInterest:
      (toCents(saving.earnedValue) - interestWithdrawnCents) / 100,
    retainedCashback:
      (toCents(saving.cashbackValue) - cashbackWithdrawnCents) / 100,
  };
};
